/*
Purpose: DBS Check expiry report (tutors) with datatable
Project: TCMS
*/

import { useState, useEffect } from "react";
import { useLocation, NavLink } from "react-router-dom";
import { Row, Col, Card } from "react-bootstrap";
import { security_key, BASE_URL } from "../global";

import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import LinearProgress from "@mui/material/LinearProgress";
import Box from "@mui/material/Box";

import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import Moment from "moment";

const DbsReport = () => {
  const location = useLocation();
  useEffect(() => {
    sessionStorage.setItem("previousPagePathname", location.pathname);
  }, [location.pathname]);

  const [loading, setLoading] = useState(true);
  const [data, setItems] = useState([]);
  const [records, setRecords] = useState([]);
  // pk_dbs_key of the certificate currently downloading (null = none in flight).
  const [downloadingKey, setDownloadingKey] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.post(BASE_URL + "API/api_index.php", {
        security_key: security_key,
        mode: "getDbsExpiryReport",
        center_key: sessionStorage.getItem("key"),
      });

      if (response.data.success === true) {
        setRecords(response.data.data);
        setItems(response.data.data);
        setLoading(false);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  // Download a certificate through the authenticated API (streams the DB blob).
  // Passing fk_cent_key scopes the fetch to this centre and forces JWT auth.
  const handleDownloadCertificate = async (pkDbsKey, fileName) => {
    // Ignore repeat clicks while a download is already in flight.
    if (downloadingKey) {
      return;
    }
    setDownloadingKey(pkDbsKey);
    try {
      const response = await axios.post(
        BASE_URL + "API/api_index.php",
        {
          security_key: security_key,
          mode: "getDbsCertificate",
          pk_dbs_key: pkDbsKey,
          fk_cent_key: sessionStorage.getItem("key"),
        },
        { responseType: "blob" }
      );

      // The backend streams the certificate with its real mime (application/pdf,
      // image/*, …) on success, but on failure echoes a JSON error body — which,
      // under responseType:"blob", still arrives as a Blob (type text/html or
      // application/json). Detect that and surface it instead of downloading a
      // bogus file.
      const blob = response.data;
      const looksLikeError =
        !blob ||
        blob.size === 0 ||
        blob.type.includes("json") ||
        blob.type.includes("text");
      if (looksLikeError) {
        toast.error("Failure! Certificate not found.", { autoClose: 700 });
        return;
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName || "dbs_certificate");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading certificate:", error);
      toast.error("Failure! Unable to download certificate. Try again later", {
        autoClose: 700,
      });
    } finally {
      setDownloadingKey(null);
    }
  };

  // Whole days from today until the certificate expires.
  // Negative = already expired; null = no expiry date on record.
  const getDaysUntilExpiry = (expiry) => {
    if (!expiry) {
      return null;
    }
    const expiryDate = Moment(expiry, "YYYY-MM-DD").startOf("day");
    if (!expiryDate.isValid()) {
      return null;
    }
    return expiryDate.diff(Moment().startOf("day"), "days");
  };

  const columns = [
    {
      headerName: "Name",
      field: "owner_name",
      headerClassName: "super-app-theme--header",
      flex: 1,
      valueGetter: (params) => {
        return `${params.row.owner_fname || ""} ${
          params.row.owner_surname || ""
        }`;
      },
    },
    {
      headerName: "Role",
      field: "owner_type",
      headerClassName: "super-app-theme--header",
      flex: 1,
      valueGetter: (params) => {
        return params.row.owner_type === "staff" ? "Staff" : "Tutor";
      },
    },
    {
      headerName: "DBS Number",
      field: "dbs_number",
      headerClassName: "super-app-theme--header",
      flex: 1,
      valueGetter: (params) => {
        return `${
          params.row.dbs_number == null || params.row.dbs_number == ""
            ? "-"
            : params.row.dbs_number
        }`;
      },
    },
    {
      headerName: "Expiry Date",
      field: "dbs_expiry_date",
      headerClassName: "super-app-theme--header",
      flex: 1,
      valueGetter: (params) => {
        if (!params.row.dbs_expiry_date) {
          return "-";
        }
        return Moment(params.row.dbs_expiry_date).format("DD-MM-YYYY");
      },
      sortComparator: (valueA, valueB, rowA, rowB, direction) => {
        if (valueA === "-" || valueB === "-") {
          return valueA === valueB ? 0 : valueA === "-" ? 1 : -1;
        }
        const dateA = Moment(valueA, "DD-MM-YYYY");
        const dateB = Moment(valueB, "DD-MM-YYYY");
        return direction === "asc" ? dateA - dateB : dateB - dateA;
      },
    },
    {
      headerName: "Days Until Expiry",
      field: "days_until_expiry",
      headerClassName: "super-app-theme--header",
      flex: 1,
      valueGetter: (params) => {
        return getDaysUntilExpiry(params.row.dbs_expiry_date);
      },
      sortComparator: (valueA, valueB) => {
        // Push rows with no expiry date to the end regardless of direction.
        if (valueA === null && valueB === null) return 0;
        if (valueA === null) return 1;
        if (valueB === null) return -1;
        return valueA - valueB;
      },
      renderCell: (params) => {
        const days = getDaysUntilExpiry(params.row.dbs_expiry_date);
        if (days === null) {
          return "-";
        }
        let color = "#2e7d32"; // green — comfortably valid
        let text;
        if (days < 0) {
          color = "#d32f2f"; // red — expired
          const n = Math.abs(days);
          text = `Expired (${n} ${n === 1 ? "day" : "days"} ago)`;
        } else if (days === 0) {
          color = "#d32f2f";
          text = "Expires today";
        } else {
          if (days <= 30) {
            color = "#ed6c02"; // amber — expiring soon
          }
          text = `${days} ${days === 1 ? "day" : "days"}`;
        }
        return <span style={{ color, fontWeight: 600 }}>{text}</span>;
      },
    },
    {
      headerName: "Certificate",
      field: "dbs_certificate_file",
      headerClassName: "super-app-theme--header",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (
          !params.row.dbs_certificate_name ||
          params.row.dbs_certificate_name === ""
        ) {
          return "-";
        }
        const isDownloading = downloadingKey === params.row.pk_dbs_key;
        // Any download in flight disables every link so we only run one at a time.
        const isDisabled = downloadingKey !== null;
        return (
          <span
            className={isDisabled ? "" : "cursor-pointer"}
            style={{
              color: isDisabled ? "#9e9e9e" : "#1976d2",
              textDecoration: "underline",
              pointerEvents: isDisabled ? "none" : "auto",
            }}
            onClick={() =>
              handleDownloadCertificate(
                params.row.pk_dbs_key,
                params.row.dbs_certificate_name
              )
            }
          >
            {isDownloading ? (
              <>
                <i className="fa-solid fa-spinner fa-spin me-1"></i>Downloading…
              </>
            ) : (
              <>
                <i className="fa-solid fa-download me-1"></i>Download
              </>
            )}
          </span>
        );
      },
    },
  ];

  return (
    <div className="">
      <ToastContainer />
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div>
          <h5 className="page-title"> DBS Check Report </h5>

          <div role="presentation">
            <Breadcrumbs aria-label="breadcrumb" className="my-breadcrumbs">
              <NavLink underline="hover" color="inherit" to="/calendar">
                {" "}
                Home
              </NavLink>
              <NavLink underline="hover" color="inherit" to="/reports">
                {" "}
                Reports
              </NavLink>
              <Typography color="text.primary">DBS Check Report </Typography>
            </Breadcrumbs>
          </div>
        </div>
      </div>

      <Card className="card my-card mb-3">
        <Card.Body>
          <Row>
            <Col xs="12">
              <div className="">
                <Row className="justify-content-center">
                  <Box
                    sx={{
                      height: "fit-content",
                      width: "100%",
                      "& .actions": {
                        color: "text.secondary",
                      },
                      "& .textPrimary": {
                        color: "text.primary",
                      },
                      "& .super-app-theme--header": {
                        backgroundColor: "#f4f4f4",
                      },
                      "& .MuiDataGrid-row": {
                        border: "1px solid #eff0f2",
                      },
                      "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus":
                        {
                          outline: "none !important",
                        },
                      "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
                        {
                          outline: "none !important",
                        },
                    }}
                    style={{ height: "100%", width: "100%" }}
                  >
                    <DataGrid
                      sx={{
                        ".MuiDataGrid-cell:focus": {
                          outline: "none",
                        },
                        "& .MuiDataGrid-row:hover": {
                          cursor: "pointer",
                        },
                      }}
                      autoHeight
                      slots={{
                        loadingOverlay: LinearProgress,
                        toolbar: GridToolbar,
                      }}
                      slotProps={{
                        toolbar: {
                          csvOptions: { fileName: "DbsCheckReport" },
                          printOptions: {
                            pageStyle: `@media print {
                                      @page {
                                        size: A4 landscape;
                                        margin: 0;
                                      }
                                    }`,
                            disableToolbarButton: true,
                            hideFooter: true,
                            hideToolbar: true,
                            fileName: "DbsCheckReport",
                          },
                          records,
                          showQuickFilter: true,
                        },
                      }}
                      initialState={{
                        ...records.initialState,
                        pagination: { paginationModel: { pageSize: 10 } },
                      }}
                      showQuickFilter={true}
                      pageSizeOptions={[10, 25, 50, 100]}
                      loading={loading}
                      rows={records.map((row, index) => ({
                        ...row,
                        id: `row-${index}`,
                      }))}
                      columns={columns}
                      rowHeight={60}
                      density="compact"
                      disableRowSelectionOnClick
                      showCellVerticalBorder={true}
                      showColumnVerticalBorder={true}
                    />
                  </Box>
                </Row>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default DbsReport;
